import { useContext, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { useTabManager } from '../../app-v2/context/tab-manager-context';
import { AlertBoardSettings, AlertBoardViewConfig } from '../../app-v2/pages/alertV2/types';
import { Role } from '../constants/auth';
import { AuthorizationContext } from '../context/authorization-context';
import {
  createLabeledEvent,
  createLabelType,
  deleteLabeledEvent,
  deleteLabelType,
  getAlertBoardSettings,
  getFilters,
  getLabeledEvents,
  getLabeledEventsByLabelTypeId,
  getLabeledEventsInRange,
  getLabelType,
  getLabelTypes,
  updateAlertBoardSettings,
  updateFilters,
  updateLabeledEvent,
  updateLabelType,
  UserSettings,
} from '../services/accounts-customize';

import { useUserId } from './accounts';

import { LabeledEvent, LabeledEventType } from '@controlrooms/models';

// TODO - move to utils
export const getTypeLabelsById = (arr: LabeledEventType[], labelIds: number[]): string | null => {
  const labels: string[] = [];

  for (const obj of arr) {
    if (labelIds.includes(obj.label_type_id as number)) {
      labels.push(obj.type_name as string);
    }

    // If the object has child_types, look inside them.
    if (obj.child_types && obj.child_types.length > 0) {
      const childLabels = obj.child_types
        .map((child) => getTypeLabelsById([child], labelIds))
        .filter(Boolean);
      if (childLabels.length) {
        labels.push(`${obj.type_name}: ${childLabels.join(', ')}`);
      }
    }
  }

  return labels.join(', ');
};

export const useLabelsAPI = () => {
  const queryClient = useQueryClient();
  const { getQueryKeyWithViewId } = useTabManager();
  const { hasRole } = useContext(AuthorizationContext);
  const hasLabelingRole = useMemo(
    () => hasRole(Role.GLOBAL_LABEL_EDITOR) || hasRole(Role.GLOBAL_LABEL_VIEWER),
    [hasRole],
  );
  const LABELED_EVENTS = 'labeledEvents';
  const LABEL_TYPE = 'labeledEvents';
  const FILTERS = 'filters';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const createLabelTypeMutation = useMutation<any, any, Partial<LabeledEventType>>(
    (labelType) => createLabelType(labelType),
    {
      onSuccess: () => queryClient.invalidateQueries(getQueryKeyWithViewId(LABEL_TYPE)),
    },
  );

  const updateLabelTypeMutation = useMutation(updateLabelType, {
    onSuccess: () => queryClient.invalidateQueries(getQueryKeyWithViewId(LABEL_TYPE)),
  });

  const deleteLabelTypeMutation = useMutation(deleteLabelType, {
    onSuccess: () => queryClient.invalidateQueries(getQueryKeyWithViewId(LABEL_TYPE)),
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const createLabeledEventMutation = useMutation<any, any, Partial<LabeledEvent>>(
    createLabeledEvent,
    {
      onSuccess: () => queryClient.invalidateQueries(getQueryKeyWithViewId(LABELED_EVENTS)),
    },
  );

  const updateLabeledEventMutation = useMutation(updateLabeledEvent, {
    onSuccess: () => queryClient.invalidateQueries(getQueryKeyWithViewId(LABELED_EVENTS)),
  });

  const deleteLabeledEventMutation = useMutation(deleteLabeledEvent, {
    onSuccess: () => queryClient.invalidateQueries(getQueryKeyWithViewId(LABELED_EVENTS)),
  });

  const updateFiltersMutation = useMutation(updateFilters, {
    onSuccess: () => queryClient.invalidateQueries(getQueryKeyWithViewId(FILTERS)),
  });

  const useLabelTypeQuery = (id: number) =>
    useQuery([getQueryKeyWithViewId(LABEL_TYPE), id], () => getLabelType(id), {
      enabled: hasLabelingRole,
    });

  const useLabelTypesQuery = () =>
    useQuery(getQueryKeyWithViewId(LABEL_TYPE), getLabelTypes, {
      enabled: hasLabelingRole,
    });

  const useLabeledEventsQuery = () =>
    useQuery([getQueryKeyWithViewId(LABELED_EVENTS)], getLabeledEvents, {
      enabled: hasLabelingRole,
    });

  const useLabeledEventsInRangeQuery = (startTime: string, endTime: string, interval: number) =>
    useQuery(
      [getQueryKeyWithViewId(LABELED_EVENTS), { startTime, endTime }],
      () =>
        getLabeledEventsInRange({ start_time: startTime, end_time: endTime, interval: interval }),
      {
        enabled: hasLabelingRole,
      },
    );

  const useLabeledEventsByLabelTypeIdQuery = (id: number) =>
    useQuery([getQueryKeyWithViewId(LABELED_EVENTS), id], () => getLabeledEventsByLabelTypeId(id), {
      enabled: hasLabelingRole,
    });

  const useFiltersQuery = () =>
    useQuery(getQueryKeyWithViewId(FILTERS), getFilters, {
      enabled: hasLabelingRole,
    });

  return {
    createLabelTypeMutation,
    updateLabelTypeMutation,
    deleteLabelTypeMutation,
    createLabeledEventMutation,
    updateLabeledEventMutation,
    deleteLabeledEventMutation,
    updateFiltersMutation,
    useLabelTypeQuery,
    useLabelTypesQuery,
    useLabeledEventsQuery,
    useLabeledEventsInRangeQuery,
    useLabeledEventsByLabelTypeIdQuery,
    useFiltersQuery,
  };
};

// Alert Board Configuration Hooks
export const useUpdateAlertBoardSettings = () => {
  const queryClient = useQueryClient();
  const userId = useUserId();

  return useMutation(
    (settings: UserSettings['settings']) => {
      if (!userId) throw new Error('User ID required');
      return updateAlertBoardSettings(userId, settings);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['ALERT_BOARD_SETTINGS', userId]);
      },
    },
  );
};

export const useGetAlertBoardSettings = () => {
  const userId = useUserId();

  return useQuery(
    ['ALERT_BOARD_SETTINGS', userId],
    () => {
      if (!userId) throw new Error('User ID required');
      return getAlertBoardSettings(userId);
    },
    {
      enabled: !!userId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );
};

// Get views for a specific alert board
export const useGetAlertBoardViews = (boardId?: string) => {
  const { data: settingsData } = useGetAlertBoardSettings();

  if (!boardId || !settingsData?.result?.settings?.alert_boards?.[boardId]) {
    return { views: [] as AlertBoardViewConfig[], defaultView: null };
  }

  const boardConfig = settingsData.result.settings.alert_boards[boardId];
  const views = boardConfig.views || [];
  const defaultView = views.find((v: AlertBoardViewConfig) => v.is_default) || views[0] || null;

  return { views, defaultView };
};

// Re-export types for convenience
export type { AlertBoardSettings, AlertBoardViewConfig };
